import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import BoldIcon from 'remixicon-react/BoldIcon';
import CodeBoxLineIcon from 'remixicon-react/CodeBoxLineIcon';
import CodeLinkIcon from 'remixicon-react/CodeLineIcon';
import DoubleQuotesLIcon from 'remixicon-react/DoubleQuotesLIcon';
import H1Icon from 'remixicon-react/H1Icon';
import H2Icon from 'remixicon-react/H2Icon';
import ItalicIcon from 'remixicon-react/ItalicIcon';
import LinkIcon from 'remixicon-react/LinkIcon';
import LinkUnlinkIcon from 'remixicon-react/LinkUnlinkIcon';
import ListOrderedIcon from 'remixicon-react/ListOrderedIcon';
import ListUnorderedIcon from 'remixicon-react/ListUnorderedIcon';
import StrikethroughIcon from 'remixicon-react/StrikethroughIcon';
import UnderlineIcon from 'remixicon-react/UnderlineIcon';
import { twMerge } from 'tailwind-merge';
import { useI18n } from '../../i18n';
import { useComposerConfig } from '../ComposerConfigContext';
import { MenuItem } from './BubbleMenuItem';
import { LinkFormDialog } from './LinkFormDialog';
export const TextFormattingMenu = ({ editor, className, }) => {
    const intl = useI18n();
    const [linkFormState, setLinkFormState] = useState({
        open: false,
        text: '',
        href: undefined,
    });
    const { extensions } = useComposerConfig();
    if (!editor) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: twMerge('overflow-auto flex items-center rounded-sm space-s-2 divide-x divide-line-subdued py-2 px-3 text-content-subdued bg-surface shadow-lg border border-line-subdued', className), children: [_jsxs("div", { className: "flex space-s-2", children: [extensions.formatting.bold && (_jsx(MenuItem, { title: intl.$t({
                                    id: 'Composer.TextBubbleMenu.Bold',
                                    defaultMessage: 'Bold',
                                }), onClick: () => editor.chain().focus().toggleBold().run(), isActive: editor.isActive('bold'), icon: _jsx(BoldIcon, {}, void 0) }, void 0)), extensions.formatting.italic && (_jsx(MenuItem, { title: intl.$t({
                                    id: 'Composer.TextBubbleMenu.Italic',
                                    defaultMessage: 'Italic',
                                }), onClick: () => editor.chain().focus().toggleItalic().run(), isActive: editor.isActive('italic'), icon: _jsx(ItalicIcon, {}, void 0) }, void 0)), extensions.formatting.underline && (_jsx(MenuItem, { title: intl.$t({
                                    id: 'Composer.TextBubbleMenu.Underline',
                                    defaultMessage: 'Underline',
                                }), onClick: () => editor.chain().focus().toggleUnderline().run(), isActive: editor.isActive('underline'), icon: _jsx(UnderlineIcon, {}, void 0) }, void 0)), extensions.formatting.strikethrough && (_jsx(MenuItem, { title: intl.$t({
                                    id: 'Composer.TextBubbleMenu.StrikeThrough',
                                    defaultMessage: 'Strike Through',
                                }), onClick: () => editor.chain().focus().toggleStrike().run(), isActive: editor.isActive('strike'), icon: _jsx(StrikethroughIcon, {}, void 0) }, void 0))] }, void 0), _jsx("div", { className: "flex ps-2", children: _jsx(MenuItem, { title: editor.isActive('custom-link') ? 'Unlink' : 'Link', onClick: () => {
                                var _a, _b;
                                const { view, state } = editor;
                                const { from, to } = view.state.selection;
                                const text = state.doc.textBetween(from, to, '');
                                const node = state.doc.nodeAt(from);
                                if (!node) {
                                    return;
                                }
                                const mark = (_a = node.marks) === null || _a === void 0 ? void 0 : _a.find(m => m.type.name === 'custom-link');
                                setLinkFormState({
                                    open: true,
                                    text,
                                    href: (_b = mark === null || mark === void 0 ? void 0 : mark.attrs) === null || _b === void 0 ? void 0 : _b.href,
                                });
                            }, isActive: editor.isActive('custom-link'), icon: editor.isActive('custom-link') ? _jsx(LinkUnlinkIcon, {}, void 0) : _jsx(LinkIcon, {}, void 0) }, void 0) }, void 0), extensions.heading && (_jsxs("div", { className: "flex space-s-2 ps-2", children: [_jsx(MenuItem, { title: intl.$t({
                                    id: 'Composer.LargeHeader',
                                    defaultMessage: 'Large Header',
                                }), onClick: () => editor.chain().focus().toggleHeading({ level: 2 }).run(), isActive: editor.isActive('heading', { level: 2 }), icon: _jsx(H1Icon, {}, void 0) }, void 0), _jsx(MenuItem, { title: intl.$t({
                                    id: 'Composer.MediumHeader',
                                    defaultMessage: 'Medium Header',
                                }), onClick: () => editor.chain().focus().toggleHeading({ level: 3 }).run(), isActive: editor.isActive('heading', { level: 3 }), icon: _jsx(H2Icon, {}, void 0) }, void 0)] }, void 0)), _jsx("div", { className: "flex ps-2", children: _jsx(MenuItem, { title: intl.$t({
                                id: 'Composer.Blockquote',
                                defaultMessage: 'Blockquote',
                            }), onClick: () => editor.chain().focus().toggleBlockquote().run(), isActive: editor.isActive('blockquote'), icon: _jsx(DoubleQuotesLIcon, {}, void 0) }, void 0) }, void 0), extensions.code && (_jsxs("div", { className: "flex space-s-2 ps-2", children: [_jsx(MenuItem, { title: intl.$t({
                                    id: 'Composer.TextBubbleMenu.InlineCode',
                                    defaultMessage: 'Inline Code',
                                }), onClick: () => editor.chain().focus().toggleCode().run(), isActive: editor.isActive('code'), icon: _jsx(CodeLinkIcon, {}, void 0) }, void 0), _jsx(MenuItem, { title: intl.$t({
                                    id: 'Composer.CodeBlock',
                                    defaultMessage: 'Code Block',
                                }), onClick: () => editor.chain().focus().toggleCodeBlock().run(), isActive: editor.isActive('codeBlock'), icon: _jsx(CodeBoxLineIcon, {}, void 0) }, void 0)] }, void 0)), extensions.list && (_jsxs("div", { className: "flex space-s-2 ps-2", children: [_jsx(MenuItem, { title: intl.$t({
                                    id: 'Composer.TextBubbleMenu.OrderedList',
                                    defaultMessage: 'Ordered List',
                                }), onClick: () => editor.chain().focus().toggleOrderedList().run(), isActive: editor.isActive('orderedList'), icon: _jsx(ListOrderedIcon, {}, void 0) }, void 0), _jsx(MenuItem, { title: intl.$t({
                                    id: 'Composer.TextBubbleMenu.BulletList',
                                    defaultMessage: 'Bullet List',
                                }), onClick: () => editor.chain().focus().toggleBulletList().run(), isActive: editor.isActive('bulletList'), icon: _jsx(ListUnorderedIcon, {}, void 0) }, void 0)] }, void 0))] }, void 0), _jsx(LinkFormDialog, { defaultValues: {
                    text: linkFormState.text,
                    link: linkFormState.href,
                }, onSubmit: data => {
                    const { link, text } = data;
                    if (link) {
                        const { from, to } = editor.view.state.selection;
                        editor
                            .chain()
                            .setTextSelection({ from, to })
                            .insertLink({ href: link, text })
                            .run();
                    }
                    else {
                        editor
                            .chain()
                            .focus()
                            .extendMarkRange('custom-link')
                            .unsetLink()
                            .run();
                    }
                    setLinkFormState({
                        text: '',
                        href: undefined,
                        open: false,
                    });
                }, open: linkFormState.open, onClose: () => setLinkFormState({
                    text: '',
                    href: undefined,
                    open: false,
                }) }, void 0)] }, void 0));
};
//# sourceMappingURL=TextBubbleMenu.js.map